import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, HeartHandshake, Check } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Section } from "@/components/Page";
import { ScrollReveal } from "@/motion";

const validate = (f) => ({
  name: f.name.trim().length < 2 ? "Please enter your full name" : "",
  phone: !/^[6-9]\d{9}$/.test(f.phone.trim()) ? "Enter a valid 10-digit Indian mobile number" : "",
  email: !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email.trim()) ? "Enter a valid email address" : "",
});

export function InsuranceSection() {
  const [f, setF] = useState({ name: "", phone: "", email: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [serverErr, setServerErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(f); setErrors(errs); setServerErr("");
    if (Object.values(errs).some(Boolean)) return;
    setBusy(true);
    try { await api.post("/insurance-enquiries", { name: f.name.trim(), phone: f.phone.trim(), email: f.email.trim() }); setDone(true); }
    catch (err) { setServerErr(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Section className="mt-24" id="insurance">
      <ScrollReveal variant="up" className="rounded-3xl border border-slate-200 bg-white overflow-hidden grid lg:grid-cols-2" data-testid="insurance-section">
        <div className="navy text-white p-8 sm:p-12 relative grain overflow-hidden">
          <div className="absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-[#0284C7]/30 blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full glass px-3 py-1.5 text-xs font-bold text-sky-200 mb-5"><ShieldCheck size={13} /> Health insurance guidance</div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Exploring Health Insurance?</h2>
            <p className="text-slate-300 mt-4 leading-relaxed max-w-md">Get guidance on health insurance options that fit you and your loved ones.</p>
            <ul className="mt-6 space-y-2.5 text-sm text-slate-200">
              {["Unbiased comparison of family floater & individual plans", "Help with cashless network hospitals near you", "Support with claims and renewals"].map((t) => <li key={t} className="flex items-start gap-2"><Check size={16} className="text-sky-300 mt-0.5 shrink-0" /> {t}</li>)}
            </ul>
          </div>
        </div>
        <div className="p-8 sm:p-12">
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div key="ok" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="h-full flex flex-col items-center justify-center text-center py-6" data-testid="insurance-success">
                <div className="h-16 w-16 rounded-full bg-emerald-500 text-white grid place-items-center mb-4"><HeartHandshake size={28} /></div>
                <h3 className="font-display text-xl font-bold text-[#06152D]">Enquiry received!</h3>
                <p className="text-sm text-[#475569] mt-2 max-w-sm">Thank you, {f.name.split(" ")[0]}. An Apnicare insurance advisor will reach out on {f.phone} within one working day.</p>
                <Button variant="outline" className="rounded-full mt-6" onClick={() => { setDone(false); setF({ name: "", phone: "", email: "" }); }} data-testid="insurance-another-button">Submit another enquiry</Button>
              </motion.div>
            ) : (
              <motion.form key="form" noValidate onSubmit={submit} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="insurance-form">
                <div className="eyebrow">Quick enquiry</div>
                <Field label="Name" error={errors.name} testid="insurance-name-error"><Input data-testid="insurance-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Your full name" className="h-12" aria-invalid={!!errors.name} /></Field>
                <Field label="Contact Number" error={errors.phone} testid="insurance-phone-error"><Input data-testid="insurance-phone-input" inputMode="numeric" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value.replace(/[^\d]/g, "").slice(0, 10) })} placeholder="10-digit mobile number" className="h-12" aria-invalid={!!errors.phone} /></Field>
                <Field label="Email ID" error={errors.email} testid="insurance-email-error"><Input data-testid="insurance-email-input" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="you@example.com" className="h-12" aria-invalid={!!errors.email} /></Field>
                {serverErr && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="insurance-server-error">{serverErr}</p>}
                <Button type="submit" disabled={busy} data-testid="insurance-submit-button" data-cursor="ENQUIRE" className="w-full h-12 rounded-full bg-[#06152D] hover:bg-[#0284C7] font-semibold">{busy ? "Submitting…" : "Submit Enquiry"}</Button>
                <p className="text-[11px] text-slate-400 text-center">Your details are private and used only to contact you about insurance guidance.</p>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </ScrollReveal>
    </Section>
  );
}

const Field = ({ label, error, testid, children }) => (
  <label className="block"><span className="text-xs font-semibold text-[#475569] mb-1 block">{label}</span>{children}{error && <span className="text-xs text-red-600 mt-1 block" data-testid={testid}>{error}</span>}</label>
);
